import {
  AttachmentShortcut,
  DataTemplateAttachment,
  defaultDataSize,
  DropdownOption,
  ExternalPdfFieldType,
  FieldFromType,
  FieldTypes,
  getInlineFieldSize,
  getUniqueRandomId,
  InlineAttachmentPlacement,
  InputTemplateAttachment,
  ShapeTemplateAttachment,
  ShapeType,
} from '@hb/shared'
import { useCallback } from 'react'
import { DEFAULT_RADIO_FONT_SIZE } from '../../PdfView/attachmentFieldStyles'
import { usePdfView } from '../../PdfView/context'
import { useExternalPdfTemplateEditor } from './context'

// Extra config only radio (DROPDOWN) attachments need at creation — everything else is type+placeholder.
export interface AddedFieldExtras {
  options?: DropdownOption[]
  radioDirection?: 'row' | 'column'
}

const getAddedField = <T extends ExternalPdfFieldType>(
  type: T,
  placeholder: string,
  extra?: AddedFieldExtras,
): FieldFromType<T> => {
  switch (type) {
    case FieldTypes.INITIALS:
      return { type, placeholder, expectedInitials: '', patientName: '' } as FieldFromType<T>
    case FieldTypes.SIGNATURE:
      return { type, placeholder, expectedFirstName: '', expectedLastName: '' } as FieldFromType<T>
    case FieldTypes.DROPDOWN:
      return {
        type,
        placeholder,
        inputType: 'radio',
        options: extra?.options ?? [],
        radioDirection: extra?.radioDirection ?? 'row',
      } as FieldFromType<T>
    default:
      return { type, placeholder } as FieldFromType<T>
  }
}

export const useAddExternalPdfInputAttachment = (pageIndex: number) => {
  const { updateExternalPdf, externalPdf } = useExternalPdfTemplateEditor()
  return useCallback(
    (type: ExternalPdfFieldType, placeholder: string, extra?: AddedFieldExtras) => {
      const newAttachment: InputTemplateAttachment<InlineAttachmentPlacement> = {
        id: getUniqueRandomId(Object.keys(externalPdf.attachments ?? {})),
        field: getAddedField(type, placeholder, extra),
        placement: {
          type: 'inline',
          pageIndex,
          orientation: {
            position: [0, 0],
            scale: [1, 1],
            rotation: 0,
          },
        },
        type: 'input',
      }
      const defaultSize = getInlineFieldSize(newAttachment)
      newAttachment.placement.orientation.position = [defaultSize[0] / 2, defaultSize[1] / 2]
      // Radio inputs resize by an explicit width/height box + editable font size (like data attachments).
      if (type === FieldTypes.DROPDOWN) {
        newAttachment.width = defaultSize[0]
        newAttachment.height = defaultSize[1]
        newAttachment.fontSize = DEFAULT_RADIO_FONT_SIZE
      }
      updateExternalPdf({
        ...externalPdf,
        attachments: {
          ...externalPdf.attachments,
          [newAttachment.id]: newAttachment,
        },
      })
    },
    [externalPdf, updateExternalPdf, pageIndex],
  )
}

export const useAddExternalPdfShapeAttachment = (pageIndex: number) => {
  const { updateExternalPdf, externalPdf } = useExternalPdfTemplateEditor()
  const { pageSizes } = usePdfView()
  return useCallback(
    (shapeType: ShapeType) => {
      const defaultSize: [number, number] =
        shapeType === 'circle' || shapeType === 'square' ? [100, 100] : [150, 75]
      // place at center of page; fall back to top-left margin if page size unavailable
      const pageSize = pageSizes[pageIndex + 1]
      const initialPosition: [number, number] = pageSize
        ? [pageSize[0] / 2, pageSize[1] / 2]
        : [defaultSize[0] / 2, defaultSize[1] / 2]
      const attachment: ShapeTemplateAttachment<InlineAttachmentPlacement> = {
        type: 'shape',
        id: getUniqueRandomId(Object.keys(externalPdf.attachments ?? {})),
        shapeType,
        width: defaultSize[0],
        height: defaultSize[1],
        fillColor: '#ffffff',
        placement: {
          type: 'inline',
          pageIndex,
          orientation: {
            position: initialPosition,
            scale: [1, 1],
            rotation: 0,
          },
        },
      }
      updateExternalPdf({
        ...externalPdf,
        attachments: { ...externalPdf.attachments, [attachment.id]: attachment },
      })
    },
    [externalPdf, updateExternalPdf, pageIndex, pageSizes],
  )
}

export const useAddExternalPdfDataAttachment = (pageIndex: number) => {
  const { updateExternalPdf, externalPdf } = useExternalPdfTemplateEditor()
  return useCallback(
    (shortcut: AttachmentShortcut) => {
      const defaultSize = defaultDataSize[shortcut.type]
      const attachment: DataTemplateAttachment<InlineAttachmentPlacement> = {
        type: 'data',
        id: getUniqueRandomId(Object.keys(externalPdf.attachments ?? {})),
        shortcut,
        height: defaultSize[1],
        width: defaultSize[0],
        placement: {
          type: 'inline',
          pageIndex,
          orientation: {
            position: [defaultSize[0] / 2, defaultSize[1] / 2],
            scale: [1, 1],
            rotation: 0,
          },
        },
      }
      updateExternalPdf({
        ...externalPdf,
        attachments: {
          ...externalPdf.attachments,
          [attachment.id]: attachment,
        },
      })
    },
    [externalPdf, updateExternalPdf, pageIndex],
  )
}
